import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Pencil, Check, Upload, Globe, Trash2, ListChecks, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, resolveAssetUrl } from "@/lib/api";
import { editorialPublicPath, fmtData, etapaInfo, PRIORIDADES, SAUDE, STATUS_CLIENTE, STATUS_CICLO, STATUS_TAREFA, POST_STATUS, redeInfo, tipoConteudoInfo, FREQ_CAPTACAO, STATUS_GRAVACAO } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CicloDrawer } from "@/components/CicloDrawer";
import { TarefaEditDialog } from "@/components/TarefaEditDialog";
import { ClienteSwitcher } from "@/components/ClienteSwitcher";
import { ClienteDrivePanel } from "@/components/ClienteDrivePanel";
import { EditingFields, EditingSchedule } from "@/components/EditingSchedule";

const nomeCompetencia = (competencia) => {
  if (!competencia || !/^\d{4}-\d{2}$/.test(competencia)) return competencia || "Sem competência";
  const [ano, mes] = competencia.split("-").map(Number);
  const nome = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(ano, mes - 1, 1)));
  return nome.charAt(0).toUpperCase() + nome.slice(1);
};

export default function ClientePage() {
  const { id } = useParams();
  return <ClienteDetail key={id} id={id} />;
}

function ClienteDetail({ id }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [confirmarEncerramento, setConfirmarEncerramento] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [excluirDialog, setExcluirDialog] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [form, setForm] = useState(null);
  const [logoDialog, setLogoDialog] = useState(false);
  const [logoSite, setLogoSite] = useState("");
  const [logoLoading, setLogoLoading] = useState(false);
  const [cicloAberto, setCicloAberto] = useState(null);
  const [equipe, setEquipe] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tarefasAbertas, setTarefasAbertas] = useState(false);
  const [tarefaEditando, setTarefaEditando] = useState(null);
  const [cicloFocoId, setCicloFocoId] = useState(null);
  const paginaAtiva = useRef(true);

  const load = useCallback(() => {
    api.get(`/clientes/${id}`).then((r) => { if (paginaAtiva.current) setData(r.data); })
      .catch(() => { if (paginaAtiva.current) navigate("/producao?view=clientes"); });
  }, [id, navigate]);
  useEffect(() => {
    paginaAtiva.current = true;
    load();
    return () => { paginaAtiva.current = false; };
  }, [load]);
  useEffect(() => {
    api.get("/equipe").then((r) => setEquipe(r.data)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="flex items-center gap-3 py-20 justify-center">
        <span className="w-2 h-2 rounded-full bg-[#F43F5E] animate-pulse" />
        <span className="font-mono text-xs tracking-widest text-[#71717A] uppercase">Abrindo cliente…</span>
      </div>
    );

  const { cliente: c, ciclos, eventos, postagens, onboarding, atividades, tarefas } = data;
  const encerrado = c.status === "encerrado";
  const tarefaSomenteLeitura = (t) => encerrado || !!t.cliente_encerrado_em || t.status === "cancelado";
  const st = STATUS_CLIENTE[c.status] || STATUS_CLIENTE.ativo;
  const sd = SAUDE[c.saude] || SAUDE.saudavel;
  const cicloAtual = !encerrado && ciclos.find((ci) => !["concluido", "cancelado"].includes(ci.status) && !ci.arquivado);
  const ciclosOrdenados = ciclos.filter((ci) => ci.status !== "cancelado").sort((a, b) => (b.competencia || "").localeCompare(a.competencia || "") || Number(!!a.cliente_encerrado_em) - Number(!!b.cliente_encerrado_em) || (b.created_at || "").localeCompare(a.created_at || ""));
  const ciclosMesVisiveis = Array.from(new Map([
    ...ciclosOrdenados.filter((ci) => ci.progresso < 100 || ci.status !== "concluido"),
    ...ciclosOrdenados.slice(0, 2),
  ].map((ci) => [ci.id, ci])).values()).sort((a, b) => (b.competencia || "").localeCompare(a.competencia || ""));
  const hoje = new Date();
  const competenciaAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  const cicloFoco = ciclosMesVisiveis.find((ci) => ci.id === cicloFocoId)
    || ciclosMesVisiveis.find((ci) => ci.competencia === competenciaAtual)
    || ciclosMesVisiveis.find((ci) => ci.competencia < competenciaAtual)
    || ciclosMesVisiveis[0] || null;
  const tarefasCicloFoco = cicloFoco ? tarefas.filter((tarefa) => tarefa.ciclo_id === cicloFoco.id) : [];
  const tarefasConcluidasFoco = tarefasCicloFoco.filter((tarefa) => tarefa.status === "concluido");
  const tarefasPendentesFoco = tarefasCicloFoco.filter((tarefa) => !["concluido", "cancelado"].includes(tarefa.status));
  const tarefasPendentes = tarefas.filter((t) => !["concluido", "cancelado"].includes(t.status));
  const templateSelecionado = templates.find((template) => template.id === c.template_id);
  const proximaGravacao = !encerrado && eventos.find((e) => ["gravacao_estudio", "captacao_externa"].includes(e.tipo) && !["cancelada", "realizada"].includes(e.status));
  const linkEditorial = `${window.location.origin}${editorialPublicPath(c)}`;

  const salvarAlteracoes = async () => {
    if (salvando) return;
    setSalvando(true);
    try {
      await api.patch(`/clientes/${c.id}`, { ...form, operacao_versao: c.operacao_versao || 0 });
      toast.success(form.status === "encerrado" ? "Cliente encerrado. Pendências canceladas e histórico preservado." : encerrado && ["ativo", "onboarding"].includes(form.status) ? "Cliente reativado com um novo ciclo." : "Cliente atualizado");
      setDialog(false);
      setConfirmarEncerramento(false);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSalvando(false); }
  };

  const salvar = (e) => {
    e.preventDefault();
    if (form.status === "encerrado" && !encerrado) setConfirmarEncerramento(true);
    else salvarAlteracoes();
  };

  const excluirCliente = async () => {
    setExcluindo(true);
    try {
      await api.delete(`/clientes/${c.id}`);
      toast.success("Cliente excluído da carteira");
      navigate("/producao?view=clientes");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setExcluindo(false);
      setExcluirDialog(false);
    }
  };

  const togglePasso = async (idx, done) => {
    const res = await api.patch(`/onboarding/${onboarding.id}/passos/${idx}`, { done });
    if (res.data.concluido) toast.success("Onboarding concluído — cliente ativo!");
    load();
  };

  const F = (k, v) => setForm((current) => ({ ...current, [k]: v }));

  const escolherTemplate = (templateId) => {
    const escolhido = templates.find((template) => template.id === templateId);
    setForm((current) => ({
      ...current,
      template_id: templateId === "auto" ? "" : templateId,
      frequencia_captacao: escolhido?.key === "conteudo_bimestral" ? "bimestral" : current.frequencia_captacao,
    }));
  };

  const alternarTarefa = async (tarefa) => {
    try {
      const concluida = tarefa.status === "concluido";
      await api.patch(`/tarefas/${tarefa.id}`, { status: concluida ? "em_andamento" : "concluido" });
      toast.success(concluida ? "Tarefa reaberta" : "Tarefa concluída");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const editarProximaAcao = () => {
    if (!cicloAtual) return;
    const tarefa = tarefas.find((item) => item.id === cicloAtual.proxima_acao?.tarefa_id);
    if (tarefa) setTarefaEditando(tarefa);
    else setCicloAberto(cicloAtual.id);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clientes/${c.id}/logo`, fd);
      toast.success("Logo atualizado");
      setLogoDialog(false);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setLogoLoading(false); e.target.value = ""; }
  };

  const extrairLogo = async () => {
    if (!logoSite.trim()) return;
    setLogoLoading(true);
    try {
      await api.post(`/clientes/${c.id}/logo-do-site`, { url: logoSite });
      toast.success("Logo extraído do site");
      setLogoDialog(false);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setLogoLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="cliente-page">
      <div className="flex flex-wrap items-center gap-4">
        <ClienteSwitcher clienteId={id} />
        <button onClick={() => navigate("/producao?view=clientes")} className="flex min-h-11 items-center gap-2 text-sm text-[#71717A] hover:text-[#8CC63F] transition-colors" data-testid="voltar-clientes">
          <ArrowLeft size={15} /> Todos os clientes
        </button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => { setLogoSite(c.site || ""); setLogoDialog(true); }} data-testid="cliente-logo-button" title="Alterar logo"
            className="relative w-14 h-14 rounded-full bg-[#8CC63F]/12 border border-[#8CC63F]/30 flex items-center justify-center font-serif text-2xl text-[#8CC63F] overflow-hidden group shrink-0">
            {c.logo ? <img src={resolveAssetUrl(c.logo)} alt={c.empresa} className="w-full h-full object-cover" /> : c.empresa[0]}
            <span className="absolute inset-0 bg-[#09090B]/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Pencil size={14} className="text-[#8CC63F]" /></span>
          </button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-serif text-3xl text-[#F4F4F0] tracking-tight">{c.empresa}</h1>
              <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ color: st.cor, borderColor: `${st.cor}55` }} data-testid="cliente-status">{st.nome}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ color: sd.cor, borderColor: `${sd.cor}55` }} data-testid="cliente-saude">{sd.nome}</span>
            </div>
            <p className="caption-label mt-2 normal-case tracking-normal">{c.tipo === "recorrente" ? "Recorrente" : "One time"} · {templateSelecionado?.nome || `captação ${c.frequencia_captacao || "—"}`}{c.instagram ? ` · ${c.instagram}` : ""}</p>
          </div>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <Button variant="outline" data-testid="cliente-copiar-editorial" onClick={() => { navigator.clipboard.writeText(linkEditorial); toast.success("Link do calendário editorial copiado"); }} className="flex-1 border-[#8CC63F]/40 text-[#8CC63F] hover:bg-[#8CC63F]/10 rounded-full sm:flex-none">
            <Copy size={14} className="mr-1" /> Calendário editorial
          </Button>
          <Button variant="outline" data-testid="cliente-edit-button" onClick={() => { setForm({ ...c }); setDialog(true); }} className="flex-1 border-[#27272A] text-[#A1A1AA] hover:text-[#F4F4F0] rounded-full sm:flex-none">
            <Pencil size={14} className="mr-1" /> Editar
          </Button>
        </div>
      </div>

      {/* Situação atual */}
      {encerrado && <div role="status" data-testid="cliente-encerrado-aviso" className="studio-card p-4 text-sm text-[#A1A1AA]">Operação encerrada. Ciclos e atividades pendentes foram cancelados. O histórico está preservado em Produção e nas tarefas. Ao reativar o cliente, um novo ciclo será criado, sem reabrir pendências antigas.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button type="button" className="studio-card p-4 text-left studio-card-hover w-full" data-testid="cliente-proxima-acao" onClick={editarProximaAcao}>
          <p className="caption-label">Próxima ação</p>
          {cicloAtual?.proxima_acao ? (
            <>
              <p className="text-sm text-[#F4F4F0] mt-2 leading-snug">{cicloAtual.proxima_acao.titulo}</p>
              <p className="font-mono text-[10px] text-[#8CC63F] mt-2">{cicloAtual.proxima_acao.responsavel || "sem responsável"} · {fmtData(cicloAtual.proxima_acao.prazo)}</p>
              <p className="font-mono text-[9px] text-[#3F3F46] mt-1 uppercase tracking-wide">derivada das tarefas do ciclo · clique para editar</p>
            </>
          ) : cicloAtual ? (
            <>
              <p className="text-xs text-[#F59E0B] mt-2">Ciclo sem próxima ação definida</p>
              <p className="font-mono text-[9px] text-[#3F3F46] mt-1 uppercase tracking-wide">clique para abrir o ciclo e adicionar</p>
            </>
          ) : <p className="text-xs text-[#71717A] mt-2">Nenhuma pendente</p>}
        </button>
        <div className="studio-card p-4" data-testid="cliente-ciclo-atual">
          <p className="caption-label">Ciclo atual</p>
          {cicloAtual ? (
            <>
              <p className="text-sm text-[#F4F4F0] mt-2">{cicloAtual.competencia} · {etapaInfo(cicloAtual.etapa).nome}</p>
              <div className="mt-2 h-1 bg-[#27272A] rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${cicloAtual.progresso}%`, backgroundColor: etapaInfo(cicloAtual.etapa).cor }} />
              </div>
            </>
          ) : <p className="text-xs text-[#71717A] mt-2">Sem ciclo ativo</p>}
        </div>
        <div className="studio-card p-4" data-testid="cliente-proxima-gravacao">
          <p className="caption-label">Próxima gravação</p>
          {proximaGravacao ? (
            <>
              <p className="text-sm text-[#F4F4F0] mt-2 leading-snug">{proximaGravacao.titulo}</p>
              <p className="font-mono text-[10px] text-[#8CC63F] mt-2">{fmtData(proximaGravacao.data)} {proximaGravacao.hora_inicio}</p>
            </>
          ) : <p className="text-xs text-[#71717A] mt-2">Nenhuma agendada</p>}
        </div>
        <button type="button" onClick={() => setTarefasAbertas(true)} className="studio-card studio-card-hover p-4 text-left w-full" data-testid="cliente-tarefas-resumo">
          <p className="caption-label">Tarefas abertas</p>
          <p className="font-mono text-2xl text-[#F4F4F0] mt-2">{tarefasPendentes.length}</p>
          <p className="font-mono text-[10px] text-[#71717A] mt-1 uppercase">{ciclos.length} ciclo(s) no histórico</p>
          <p className="mt-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[#8CC63F]">
            <ListChecks size={13} /> {encerrado ? "Consultar histórico" : "Ver e editar todas"}
          </p>
        </button>
      </div>

      {c.tipo === "recorrente" && cicloFoco && !encerrado && !["concluido", "cancelado"].includes(cicloFoco.status) && (!tarefasCicloFoco.some(t => t.edicao_grupo) || tarefasCicloFoco.some(t => t.edicao_pendente)) && <EditingSchedule key={cicloFoco.id} ciclo={cicloFoco} onChanged={load} />}
      <section className="studio-card overflow-hidden" data-testid="cliente-evolucao-mensal">
        <div className="flex flex-col gap-2 border-b border-[#27272A] px-5 py-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CalendarRange size={16} className="text-[#8CC63F]" />
              <p className="caption-label text-[#8CC63F]">Evolução mês a mês</p>
            </div>
            <h2 className="mt-1 font-serif text-2xl text-[#F4F4F0]">O que foi feito e o que ainda falta</h2>
          </div>
          <p className="max-w-xl text-xs leading-relaxed text-[#71717A]">Os dois ciclos mais recentes ficam disponíveis. Ciclos incompletos continuam visíveis até chegarem a 100%. Cancelados ficam no histórico de Produção.</p>
        </div>

        {ciclosMesVisiveis.length === 0 ? (
          <p className="px-5 py-6 text-sm text-[#71717A]">{encerrado ? "Sem ciclo ativo. Consulte os ciclos anteriores na aba Produção." : "Este cliente ainda não possui ciclos mensais ativos ou concluídos."}</p>
        ) : (
          <div className="p-4 sm:p-5">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {ciclosMesVisiveis.map((ci) => {
                const selecionado = cicloFoco?.id === ci.id;
                const completo = ci.progresso === 100;
                return (
                  <button
                    key={ci.id}
                    type="button"
                    data-testid={`cliente-bateria-${ci.id}`}
                    onClick={() => setCicloFocoId(ci.id)}
                    className={`rounded-xl border p-4 text-left transition-colors ${selecionado ? "border-[#8CC63F]/70 bg-[#8CC63F]/8" : "border-[#27272A] bg-[#141418] hover:border-[#52525B]"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-[#F4F4F0]">{nomeCompetencia(ci.competencia)}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-[#71717A]">{ci.tarefas_concluidas}/{ci.tarefas_total} etapas concluídas</p>
                      </div>
                      <span className={`rounded-full border px-2 py-1 font-mono text-[10px] ${completo ? "border-[#4ADE80]/40 text-[#4ADE80]" : "border-[#F59E0B]/40 text-[#F59E0B]"}`}>
                        {ci.progresso}%
                      </span>
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#27272A]" aria-label={`${ci.progresso}% concluído`}>
                      <div className="h-full rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#8CC63F] transition-all" style={{ width: `${ci.progresso}%` }} />
                    </div>
                    <p className={`mt-3 text-xs ${completo ? "text-[#4ADE80]" : "text-[#A1A1AA]"}`}>
                      {completo ? "Todas as etapas concluídas" : ci.proxima_acao ? `Próxima: ${ci.proxima_acao.titulo}` : "Ainda existem etapas para revisar"}
                    </p>
                  </button>
                );
              })}
            </div>

            {cicloFoco && (
              <div className="mt-5 rounded-xl border border-[#27272A] bg-[#0E0E11]" data-testid="cliente-ciclo-mensal-aberto">
                <div className="flex flex-col gap-3 border-b border-[#27272A] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="caption-label">Ciclo selecionado</p>
                    <p className="mt-1 text-base font-semibold text-[#F4F4F0]">{nomeCompetencia(cicloFoco.competencia)} · {cicloFoco.progresso}% concluído</p>
                  </div>
                  <button type="button" onClick={() => setCicloAberto(cicloFoco.id)} className="self-start rounded-full border border-[#8CC63F]/40 px-3 py-2 font-mono text-[10px] uppercase tracking-wide text-[#8CC63F] hover:bg-[#8CC63F]/10 sm:self-auto">
                    {cicloFoco.cliente_encerrado_em ? "Consultar ciclo" : "Ajustar ciclo"}
                  </button>
                </div>

                <div className="grid gap-5 p-4 lg:grid-cols-2">
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <p className="caption-label text-[#F59E0B]">A fazer</p>
                      <span className="font-mono text-xs text-[#F59E0B]">{tarefasPendentesFoco.length}</span>
                    </div>
                    <div className="space-y-2">
                      {tarefasPendentesFoco.length === 0 && <p className="rounded-lg border border-[#27272A] px-3 py-4 text-xs text-[#71717A]">Nenhuma etapa pendente.</p>}
                      {tarefasPendentesFoco.map((tarefa) => (
                        <div key={tarefa.id} className="flex items-center gap-3 rounded-lg border border-[#27272A] bg-[#141418] px-3 py-3">
                          <button type="button" disabled={tarefaSomenteLeitura(tarefa)} onClick={() => alternarTarefa(tarefa)} aria-label="Marcar tarefa como concluída" className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-[#52525B] hover:border-[#8CC63F]" />
                          <button type="button" disabled={tarefaSomenteLeitura(tarefa)} onClick={() => setTarefaEditando(tarefa)} className="min-w-0 flex-1 text-left">
                            <p className="text-sm font-semibold text-[#E4E4E0]">{tarefa.titulo}</p>
                            <p className="mt-1 font-mono text-[10px] text-[#71717A]">{tarefa.responsavel || "Sem responsável"} · {fmtData(tarefa.prazo)}</p>
                          </button>
                          <button type="button" disabled={tarefaSomenteLeitura(tarefa)} onClick={() => setTarefaEditando(tarefa)} aria-label={`Editar ${tarefa.titulo}`} className="shrink-0 p-2 text-[#71717A] hover:text-[#8CC63F]"><Pencil size={13} /></button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <p className="caption-label text-[#4ADE80]">Concluídas</p>
                      <span className="font-mono text-xs text-[#4ADE80]">{tarefasConcluidasFoco.length}</span>
                    </div>
                    <div className="space-y-2">
                      {tarefasConcluidasFoco.length === 0 && <p className="rounded-lg border border-[#27272A] px-3 py-4 text-xs text-[#71717A]">Nenhuma etapa concluída ainda.</p>}
                      {tarefasConcluidasFoco.map((tarefa) => (
                        <div key={tarefa.id} className="flex items-center gap-3 rounded-lg border border-[#4ADE80]/20 bg-[#4ADE80]/5 px-3 py-3">
                          <button type="button" disabled={tarefaSomenteLeitura(tarefa)} onClick={() => alternarTarefa(tarefa)} aria-label="Reabrir tarefa" className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-[#4ADE80] bg-[#4ADE80]"><Check size={13} className="text-[#09090B]" /></button>
                          <button type="button" disabled={tarefaSomenteLeitura(tarefa)} onClick={() => setTarefaEditando(tarefa)} className="min-w-0 flex-1 text-left">
                            <p className="text-sm font-semibold text-[#71717A] line-through">{tarefa.titulo}</p>
                            <p className="mt-1 font-mono text-[10px] text-[#71717A]">{tarefa.responsavel || "Sem responsável"} · {fmtData(tarefa.prazo)}</p>
                          </button>
                          <button type="button" disabled={tarefaSomenteLeitura(tarefa)} onClick={() => setTarefaEditando(tarefa)} aria-label={`Editar ${tarefa.titulo}`} className="shrink-0 p-2 text-[#71717A] hover:text-[#8CC63F]"><Pencil size={13} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <Tabs key={onboarding?.id || "sem-onboarding"} defaultValue={onboarding ? "onboarding" : "geral"}>
        <TabsList className="h-auto w-full border border-[#27272A] bg-[#141418]">
          <TabsTrigger value="geral" data-testid="tab-geral">Visão geral</TabsTrigger>
          {onboarding && <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding</TabsTrigger>}
          <TabsTrigger value="producao" data-testid="tab-producao-cliente">Produção ({ciclos.length})</TabsTrigger>
          <TabsTrigger value="conteudo" data-testid="tab-conteudo">Conteúdo ({postagens.length})</TabsTrigger>
          <TabsTrigger value="calendario" data-testid="tab-calendario-cliente">Calendário ({eventos.length})</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="geral" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="studio-card p-5 space-y-3">
              <p className="caption-label">Dados</p>
              {[["Contato", c.nome], ["E-mail", c.email], ["WhatsApp", c.whatsapp], ["Instagram", c.instagram], ["Site", c.site], ["Responsável interno", c.responsavel_interno], ["Cliente desde", fmtData(c.data_inicio)], ["SLA", c.sla]].map(([l, v]) => (
                <div key={l} className="flex justify-between gap-4 text-sm">
                  <span className="text-[#71717A]">{l}</span>
                  <span className="text-[#E4E4E0] text-right break-all">{v || "—"}</span>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              <div className="studio-card p-5">
                <p className="caption-label">Serviços contratados</p>
                <p className="text-sm text-[#E4E4E0] mt-2">{c.rotina || "—"}</p>
                {c.entregas_mes > 0 && <p className="font-mono text-xs text-[#8CC63F] mt-2">{c.entregas_mes} entregas/mês</p>}
              </div>
              <ClienteDrivePanel cliente={c} onChanged={load} />
              {c.notas && (
                <div className="studio-card p-5">
                  <p className="caption-label">Observações</p>
                  <p className="text-sm text-[#A1A1AA] mt-2">{c.notas}</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {onboarding && (
          <TabsContent value="onboarding" className="mt-6">
            <div className="studio-card p-5 max-w-xl" data-testid="onboarding-checklist">
              <p className="caption-label mb-4">Onboarding do cliente · {onboarding.passos.filter((p) => p.done).length}/{onboarding.passos.length}</p>
              <div className="space-y-2">
                {onboarding.passos.map((p, i) => (
                  <button key={i} data-testid={`onboarding-passo-${i}`} onClick={() => togglePasso(i, !p.done)}
                    className={`w-full flex items-center gap-3 border rounded-lg px-4 py-3 text-left transition-colors ${p.done ? "border-[#4ADE80]/30 bg-[#4ADE80]/5" : "border-[#27272A] hover:border-[#3F3F46]"}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${p.done ? "bg-[#4ADE80] border-[#4ADE80]" : "border-[#3F3F46]"}`}>
                      {p.done && <Check size={11} className="text-[#09090B]" />}
                    </span>
                    <span className={`text-sm flex-1 ${p.done ? "text-[#71717A] line-through" : "text-[#E4E4E0]"}`}>{p.nome}</span>
                    <span className="font-mono text-[10px] text-[#71717A]">{fmtData(p.data)}</span>
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>
        )}

        <TabsContent value="producao" className="mt-6 space-y-2">
          {ciclos.length === 0 && <p className="text-sm text-[#71717A]">Nenhum ciclo ainda. Crie um em Produção.</p>}
          {ciclos.map((ci) => {
            const et = etapaInfo(ci.etapa);
            const sci = STATUS_CICLO[ci.status] || STATUS_CICLO.em_andamento;
            return (
              <button key={ci.id} data-testid={`cliente-ciclo-${ci.id}`} onClick={() => setCicloAberto(ci.id)} title="Abrir editor do ciclo"
                className="studio-card studio-card-hover w-full px-4 py-3 flex flex-wrap items-center gap-3 text-left">
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm text-[#E4E4E0]">{ci.titulo}</p>
                  <p className="caption-label normal-case tracking-normal mt-0.5">{ci.proxima_acao ? `→ ${ci.proxima_acao.titulo}` : "sem próxima ação"}</p>
                </div>
                <span className="font-mono text-[10px] uppercase" style={{ color: et.cor }}>{et.nome}</span>
                <span className="font-mono text-[10px] uppercase" style={{ color: sci.cor }}>{sci.nome}</span>
                <span className="font-mono text-xs text-[#71717A]">{ci.progresso}%</span>
              </button>
            );
          })}
        </TabsContent>

        <TabsContent value="conteudo" className="mt-6 space-y-2">
          {postagens.length === 0 && <p className="text-sm text-[#71717A]">Nenhum conteúdo planejado.</p>}
          {postagens.map((p) => {
            const rede = redeInfo(p.rede_social);
            const ps = POST_STATUS[p.status] || POST_STATUS.planejado;
            return (
              <div key={p.id} data-testid={`cliente-post-${p.id}`} className="studio-card px-4 py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm text-[#E4E4E0]">{p.titulo}</p>
                  <p className="caption-label normal-case tracking-normal mt-0.5" style={{ color: rede.cor }}>{rede.nome} · {tipoConteudoInfo(p.tipo_conteudo).nome}</p>
                </div>
                <span className="font-mono text-xs text-[#8CC63F]">{fmtData(p.data)}</span>
                <Select value={p.status} onValueChange={(v) => api.patch(`/postagens/${p.id}`, { status: v }).then(() => { toast.success("Status atualizado"); load(); }).catch((err) => toast.error(apiError(err)))}>
                  <SelectTrigger data-testid={`cliente-post-status-${p.id}`} className="h-8 w-40 bg-[#18181C] border-[#27272A] text-[10px] font-mono uppercase" style={{ color: ps.cor }}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {Object.entries(POST_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="calendario" className="mt-6 space-y-2">
          {eventos.length === 0 && <p className="text-sm text-[#71717A]">Nenhum compromisso futuro.</p>}
          {eventos.map((e) => {
            const sg = STATUS_GRAVACAO[e.status] || null;
            return (
              <div key={e.id} data-testid={`cliente-evento-${e.id}`} className="studio-card px-4 py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm text-[#E4E4E0]">{e.titulo}</p>
                  <p className="caption-label normal-case tracking-normal mt-0.5">{e.local || ""}</p>
                </div>
                <span className="font-mono text-xs text-[#8CC63F]">{fmtData(e.data)} {e.hora_inicio}</span>
                {sg && (
                  <Select disabled={encerrado || !!e.cliente_encerrado_em} value={e.status || "agendada"} onValueChange={(v) => api.patch(`/eventos/${e.id}`, { status: v }).then(() => { toast.success("Gravação atualizada"); load(); }).catch((err) => toast.error(apiError(err)))}>
                    <SelectTrigger data-testid={`cliente-evento-status-${e.id}`} className="h-8 w-40 bg-[#18181C] border-[#27272A] text-[10px] font-mono uppercase" style={{ color: sg.cor }}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      {Object.entries(STATUS_GRAVACAO).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="historico" className="mt-6">
          <div className="studio-card p-5 max-w-2xl">
            {atividades.length === 0 && <p className="text-sm text-[#71717A]">Sem registros ainda.</p>}
            <div className="space-y-0">
              {atividades.map((a) => (
                <div key={a.id} data-testid={`atividade-${a.id}`} className="flex gap-3 pb-4 relative">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8CC63F] mt-1.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-[#E4E4E0]">{a.detalhe || a.acao}</p>
                    <p className="caption-label mt-1">{a.ator} · {new Date(a.created_at).toLocaleString("pt-BR")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Sheet open={tarefasAbertas} onOpenChange={setTarefasAbertas}>
        <SheetContent className="w-full overflow-y-auto border-l border-[#27272A] bg-[#0E0E11] text-[#F4F4F0] sm:max-w-2xl">
          <SheetHeader className="pr-8 text-left">
            <SheetTitle className="font-serif text-2xl text-[#F4F4F0]">Tarefas de <span className="gold-italic">{c.empresa}</span></SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-5" data-testid="cliente-tarefas-painel">
            <div className="grid grid-cols-3 gap-2">
              <div className="studio-card p-3">
                <p className="caption-label">Abertas</p>
                <p className="mt-1 font-mono text-xl text-[#F4F4F0]">{tarefasPendentes.length}</p>
              </div>
              <div className="studio-card p-3">
                <p className="caption-label">Concluídas</p>
                <p className="mt-1 font-mono text-xl text-[#4ADE80]">{tarefas.filter((t) => t.status === "concluido").length}</p>
              </div>
              <div className="studio-card p-3">
                <p className="caption-label">Total</p>
                <p className="mt-1 font-mono text-xl text-[#8CC63F]">{tarefas.length}</p>
              </div>
            </div>

            <div className="rounded-lg border border-[#8CC63F]/25 bg-[#8CC63F]/5 px-4 py-3">
              <p className="text-xs font-semibold text-[#8CC63F]">Tarefas sincronizadas</p>
              <p className="mt-1 text-xs leading-relaxed text-[#A1A1AA]">Título, prazo, responsável, etapa e conclusão são os mesmos na ficha do cliente e na Produção. Atividades de um período encerrado ficam somente para consulta. Canceladas: {tarefas.filter((t) => t.status === "cancelado").length}.</p>
            </div>

            <div className="space-y-2">
              {tarefas.length === 0 && <p className="text-sm text-[#71717A]">Este cliente ainda não possui tarefas.</p>}
              {tarefas.map((tarefa) => {
                const concluida = tarefa.status === "concluido";
                const status = STATUS_TAREFA[tarefa.status] || STATUS_TAREFA.nao_iniciado;
                const prioridade = PRIORIDADES[tarefa.prioridade] || PRIORIDADES.media;
                const etapa = etapaInfo(tarefa.etapa);
                const ciclo = ciclos.find((item) => item.id === tarefa.ciclo_id);
                return (
                  <div key={tarefa.id} data-testid={`cliente-tarefa-${tarefa.id}`} className={`rounded-xl border px-4 py-3 transition-colors ${concluida ? "border-[#27272A] bg-[#141418]/60" : "border-[#27272A] bg-[#141418] hover:border-[#3F3F46]"}`}>
                    <div className="flex items-start gap-3">
                      <button type="button" disabled={tarefaSomenteLeitura(tarefa)} onClick={() => alternarTarefa(tarefa)} data-testid={`cliente-tarefa-toggle-${tarefa.id}`} aria-label={concluida ? "Reabrir tarefa" : "Marcar tarefa como concluída"}
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${concluida ? "border-[#4ADE80] bg-[#4ADE80]" : "border-[#52525B] hover:border-[#8CC63F]"}`}>
                        {concluida && <Check size={13} className="text-[#09090B]" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-semibold leading-snug ${concluida ? "text-[#71717A] line-through" : "text-[#E4E4E0]"}`}>{tarefa.titulo}</p>
                        <p className="mt-1 font-mono text-[10px] text-[#71717A]">{ciclo?.titulo || "Sem ciclo vinculado"}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-wide">
                          <span style={{ color: etapa.cor }}>{etapa.nome}</span>
                          <span style={{ color: status.cor }}>{status.nome}</span>
                          <span style={{ color: prioridade.cor }}>{prioridade.nome}</span>
                          <span className="text-[#A1A1AA]">{fmtData(tarefa.prazo)}</span>
                          {tarefa.responsavel && <span className="text-[#A1A1AA]">{tarefa.responsavel}</span>}
                        </div>
                      </div>
                      <button type="button" disabled={tarefaSomenteLeitura(tarefa)} onClick={() => setTarefaEditando(tarefa)} data-testid={`cliente-tarefa-editar-${tarefa.id}`} aria-label="Editar tarefa"
                        className="shrink-0 rounded-full border border-[#3F3F46] p-2 text-[#A1A1AA] transition-colors hover:border-[#8CC63F]/50 hover:text-[#8CC63F]">
                        <Pencil size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <TarefaEditDialog
        tarefa={tarefaEditando}
        equipe={equipe}
        onOpenChange={(open) => { if (!open) setTarefaEditando(null); }}
        onSaved={load}
      />

      <CicloDrawer cicloId={cicloAberto} onClose={() => setCicloAberto(null)} onChange={load} equipe={equipe} templates={templates} />

      <Dialog open={logoDialog} onOpenChange={setLogoDialog}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Logo do <span className="gold-italic">cliente</span></DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2" data-testid="logo-dialog">
            <label className={`studio-card studio-card-hover p-5 flex flex-col items-center gap-2 cursor-pointer ${logoLoading ? "opacity-50 pointer-events-none" : ""}`} data-testid="logo-upload-label">
              <Upload size={18} className="text-[#8CC63F]" />
              <span className="text-sm text-[#E4E4E0]">{logoLoading ? "Enviando…" : "Enviar arquivo"}</span>
              <span className="caption-label">PNG, JPG, SVG ou WebP · até 5 MB</span>
              <input data-testid="logo-upload-input" type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={logoLoading} />
            </label>
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-[#27272A]" /><span className="caption-label">ou</span><div className="h-px flex-1 bg-[#27272A]" />
            </div>
            <div>
              <Label className="caption-label">Extrair do site da empresa</Label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="logo-site-input" value={logoSite} onChange={(e) => setLogoSite(e.target.value)} placeholder="https://empresa.com.br" className="bg-[#18181C] border-[#27272A]" />
                <Button data-testid="logo-extrair-button" onClick={extrairLogo} disabled={logoLoading || !logoSite.trim()} variant="outline" className="border-[#8CC63F]/40 text-[#8CC63F] hover:bg-[#8CC63F]/10 rounded-full shrink-0">
                  <Globe size={14} className="mr-1" /> Extrair
                </Button>
              </div>
              <p className="caption-label normal-case tracking-normal mt-2">Buscamos automaticamente o ícone/logo oficial publicado no site.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Editar <span className="gold-italic">cliente</span></DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={salvar} className="space-y-4 mt-2">
              {form.status === "encerrado" && <p role="status" className="rounded-lg border border-[#F59E0B]/40 p-3 text-sm text-[#F59E0B]">Ao salvar, os ciclos, tarefas e compromissos pendentes serão cancelados. Concluídos, contratos, pagamentos e calendário editorial serão preservados. Cancelamentos no Google com convidados continuarão exigindo sua aprovação.</p>}
              {encerrado && ["ativo", "onboarding"].includes(form.status) && <p role="status" className="rounded-lg border border-[#8CC63F]/40 p-3 text-sm text-[#8CC63F]">Ao salvar, um novo ciclo será criado com o template escolhido. As atividades antigas não serão reabertas.</p>}
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="caption-label">Empresa</Label><Input data-testid="edit-empresa" value={form.empresa} onChange={(e) => F("empresa", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Contato</Label><Input data-testid="edit-nome" value={form.nome || ""} onChange={(e) => F("nome", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Status</Label>
                  <Select value={form.status} onValueChange={(v) => F("status", v)}>
                    <SelectTrigger data-testid="edit-status" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      {Object.entries(STATUS_CLIENTE).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="caption-label">Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => F("tipo", v)}>
                    <SelectTrigger data-testid="edit-tipo" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      <SelectItem value="avulso">One time</SelectItem>
                      <SelectItem value="recorrente">Recorrente</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label className="caption-label">WhatsApp</Label><Input data-testid="edit-whatsapp" value={form.whatsapp || ""} onChange={(e) => F("whatsapp", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">E-mail</Label><Input data-testid="edit-email" value={form.email || ""} onChange={(e) => F("email", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Instagram</Label><Input data-testid="edit-instagram" value={form.instagram || ""} onChange={(e) => F("instagram", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Site</Label><Input data-testid="edit-site" value={form.site || ""} onChange={(e) => F("site", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Responsável interno</Label>
                  <Select value={form.responsavel_interno || "none"} onValueChange={(v) => F("responsavel_interno", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="edit-responsavel" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      <SelectItem value="none">—</SelectItem>
                      {equipe.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome} · {m.funcao}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="caption-label">Frequência de captação</Label>
                  <Select value={form.frequencia_captacao || "mensal"} onValueChange={(v) => F("frequencia_captacao", v)}>
                    <SelectTrigger data-testid="edit-freq" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      {FREQ_CAPTACAO.map((f) => <SelectItem key={f.key} value={f.key}>{f.nome}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="caption-label">Ciclo operacional</Label>
                  <Select value={form.template_id || "auto"} onValueChange={escolherTemplate}>
                    <SelectTrigger data-testid="edit-template" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      <SelectItem value="auto">Automático pela frequência</SelectItem>
                      {templates.map((template) => <SelectItem key={template.id} value={template.id}>{template.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1.5 text-[10px] leading-relaxed text-[#71717A]">Define as tarefas dos próximos ciclos. Os ciclos já iniciados não são alterados.</p>
                </div>
                <div><Label className="caption-label">SLA</Label><Input data-testid="edit-sla" value={form.sla || ""} onChange={(e) => F("sla", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: aprovação em 48h" /></div>
                <div><Label className="caption-label">URL do logotipo</Label><Input data-testid="edit-logo" value={form.logo || ""} onChange={(e) => F("logo", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="https://… (imagem quadrada)" /></div>
              </div>
              {form.tipo === "recorrente" && <EditingFields form={form} onChange={F} />}
              <div><Label className="caption-label">Serviços contratados</Label><Input data-testid="edit-rotina" value={form.rotina || ""} onChange={(e) => F("rotina", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Observações</Label><Textarea data-testid="edit-notas" value={form.notas || ""} onChange={(e) => F("notas", e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" rows={2} /></div>
              <Button data-testid="edit-submit" type="submit" disabled={salvando} className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">{salvando ? "Salvando…" : "Salvar"}</Button>
              {user?.role === "admin" && (
                <button type="button" data-testid="excluir-cliente-button" onClick={() => { setDialog(false); setExcluirDialog(true); }}
                  className="w-full flex items-center justify-center gap-2 rounded-full border border-[#F43F5E]/40 px-4 py-2 text-sm text-[#F43F5E] hover:bg-[#F43F5E]/10 transition-colors">
                  <Trash2 size={14} /> Excluir cliente
                </button>
              )}
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmarEncerramento} onOpenChange={(open) => { if (!salvando) setConfirmarEncerramento(open); }}>
        <AlertDialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0]">
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar a operação de {c.empresa}?</AlertDialogTitle>
            <AlertDialogDescription>Todos os ciclos e atividades ainda pendentes serão cancelados e sairão das listas operacionais. O que já foi concluído permanece no histórico. Contratos, pagamentos e calendário editorial não serão alterados.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={salvando}>Voltar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirmar-encerramento-cliente" disabled={salvando} onClick={(e) => { e.preventDefault(); salvarAlteracoes(); }}>{salvando ? "Encerrando…" : "Encerrar e cancelar pendências"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={excluirDialog} onOpenChange={setExcluirDialog}>
        <AlertDialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Excluir {c.empresa}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              O cliente será removido da carteira e não aparecerá nas abas Ativos ou Encerrados. Contratos, lançamentos e históricos relacionados serão preservados nas áreas próprias.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluindo} className="border-[#27272A] bg-transparent text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#F4F4F0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirmar-exclusao-cliente" disabled={excluindo} onClick={excluirCliente}
              className="bg-[#F43F5E] text-white hover:bg-[#E11D48]">
              {excluindo ? "Excluindo…" : "Sim, excluir cliente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
